import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Paper,
  InputLabel,
} from '@mui/material';
import { useTranslation } from '../../../app/LanguageContext';
import { CurrencyTextField, CostField, COMPACT_COST_FIELD_WIDTH } from '../../core/public';
import { buildVariantTitle } from './productVariantUtils';

const cloneVariants = (rows) =>
  (rows || []).map((v) => ({
    id: v.id,
    code: v.code,
    cost: v.cost ?? 0,
    price: v.price ?? 0,
    stock: v.stock ?? 0,
    status: v.status ?? 'ACTIVE',
    is_made_to_order: Boolean(v.is_made_to_order),
    manufacturing_days: v.manufacturing_days ?? 0,
    attributes: { ...(v.attributes || {}) },
  }));

const VARIANT_STATUS_OPTIONS = [
  { value: 'ACTIVE', labelKey: 'products.variant_status_active' },
  { value: 'INACTIVE', labelKey: 'products.variant_status_inactive' },
];

const defaultBulkValueForAction = (action) => {
  if (action === 'status') return 'ACTIVE';
  if (action === 'cost' || action === 'price') return 0;
  return '';
};

function BulkActionValueField({ action, value, onChange, translate }) {
  if (!action) {
    return (
      <TextField
        size="small"
        label={translate('products.bulk_value_label')}
        value=""
        disabled
        sx={{ width: 160 }}
      />
    );
  }

  if (action === 'status') {
    return (
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>{translate('products.bulk_value_status_label')}</InputLabel>
        <Select
          label={translate('products.bulk_value_status_label')}
          value={value || 'ACTIVE'}
          onChange={(e) => onChange(e.target.value)}
        >
          {VARIANT_STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {translate(opt.labelKey)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (action === 'cost') {
    return (
      <CostField
        size="small"
        label={translate('products.bulk_value_cost_label')}
        value={typeof value === 'number' ? value : 0}
        onChange={onChange}
        sx={{ width: 168 }}
      />
    );
  }

  if (action === 'price') {
    return (
      <CurrencyTextField
        size="small"
        label={translate('products.bulk_value_price_label')}
        value={typeof value === 'number' ? value : 0}
        onChange={onChange}
        sx={{ width: 168 }}
      />
    );
  }

  if (action === 'stock') {
    return (
      <TextField
        size="small"
        type="number"
        label={translate('products.bulk_value_stock_label')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        helperText={translate('products.bulk_value_stock_hint')}
        inputProps={{ step: 1 }}
        sx={{ width: 160 }}
      />
    );
  }

  if (action === 'days') {
    return (
      <TextField
        size="small"
        type="number"
        label={translate('products.bulk_value_days_label')}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={translate('products.manufacturing_days_short_placeholder')}
        helperText={translate('products.bulk_value_days_hint')}
        inputProps={{ min: 0, step: 1 }}
        sx={{ width: 160 }}
      />
    );
  }

  return null;
}

const canApplyBulkAction = (action, value) => {
  if (!action) return false;
  if (action === 'status') return value === 'ACTIVE' || value === 'INACTIVE';
  if (action === 'days') return true;
  if (action === 'cost' || action === 'price') {
    return typeof value === 'number' && Number.isFinite(value);
  }
  if (action === 'stock') return value !== '' && !Number.isNaN(parseFloat(value));
  return false;
};

export default function VariantBulkEditDialog({
  open,
  variants = [],
  options = [],
  onClose,
  onSave,
  saving = false,
}) {
  const { translate } = useTranslation();
  const [drafts, setDrafts] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');

  useEffect(() => {
    if (open) {
      const cloned = cloneVariants(variants);
      setDrafts(cloned);
      setSelected(new Set(cloned.map((v) => v.id)));
      setBulkAction('');
      setBulkValue('');
    }
  }, [open, variants]);

  const allSelected = drafts.length > 0 && selected.size === drafts.length;

  const toggleAll = (checked) => {
    setSelected(checked ? new Set(drafts.map((v) => v.id)) : new Set());
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateDraft = (id, patch) => {
    setDrafts((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const handleBulkActionChange = (nextAction) => {
    setBulkAction(nextAction);
    setBulkValue(defaultBulkValueForAction(nextAction));
  };

  const applyBulkAction = () => {
    if (!canApplyBulkAction(bulkAction, bulkValue) || selected.size === 0) return;

    setDrafts((prev) =>
      prev.map((row) => {
        if (!selected.has(row.id)) return row;
        if (bulkAction === 'cost') return { ...row, cost: typeof bulkValue === 'number' ? bulkValue : 0 };
        if (bulkAction === 'price') return { ...row, price: typeof bulkValue === 'number' ? bulkValue : 0 };
        if (bulkAction === 'status') {
          return { ...row, status: bulkValue === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE' };
        }
        if (bulkAction === 'stock') {
          const delta = parseFloat(bulkValue) || 0;
          return { ...row, stock: Math.max(0, (row.stock ?? 0) + delta) };
        }
        if (bulkAction === 'days') {
          return {
            ...row,
            manufacturing_days: bulkValue === '' ? 0 : parseInt(bulkValue, 10) || 0,
          };
        }
        return row;
      }),
    );
    setBulkAction('');
    setBulkValue('');
  };

  const changedRows = useMemo(() => {
    const original = new Map(variants.map((v) => [v.id, v]));
    return drafts.filter((d) => {
      const o = original.get(d.id);
      if (!o) return true;
      return (
        d.cost !== (o.cost ?? 0) ||
        d.price !== (o.price ?? 0) ||
        d.stock !== (o.stock ?? 0) ||
        d.status !== (o.status ?? 'ACTIVE') ||
        d.is_made_to_order !== Boolean(o.is_made_to_order) ||
        d.manufacturing_days !== (o.manufacturing_days ?? 0)
      );
    });
  }, [drafts, variants]);

  const handleSave = () => {
    onSave?.(changedRows);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{translate('products.bulk_edit_title')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('products.bulk_filtered_hint')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {translate('products.bulk_selected_count').replace('{count}', String(selected.size))}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{translate('products.bulk_apply_label')}</InputLabel>
            <Select
              label={translate('products.bulk_apply_label')}
              value={bulkAction}
              onChange={(e) => handleBulkActionChange(e.target.value)}
            >
              <MenuItem value="cost">{translate('products.bulk_action_cost')}</MenuItem>
              <MenuItem value="price">{translate('products.bulk_action_price')}</MenuItem>
              <MenuItem value="status">{translate('products.bulk_action_status')}</MenuItem>
              <MenuItem value="stock">{translate('products.bulk_action_stock')}</MenuItem>
              <MenuItem value="days">{translate('products.bulk_action_days')}</MenuItem>
            </Select>
          </FormControl>
          <BulkActionValueField
            action={bulkAction}
            value={bulkValue}
            onChange={setBulkValue}
            translate={translate}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={applyBulkAction}
            disabled={!canApplyBulkAction(bulkAction, bulkValue)}
          >
            {translate('common.apply')}
          </Button>
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox checked={allSelected} indeterminate={selected.size > 0 && !allSelected} onChange={(e) => toggleAll(e.target.checked)} />
                </TableCell>
                <TableCell>{translate('products.variant')}</TableCell>
                <TableCell>{translate('core.cost.label')}</TableCell>
                <TableCell>{translate('products.price')}</TableCell>
                <TableCell>{translate('products.stock')}</TableCell>
                <TableCell>{translate('products.manufacturing_days')}</TableCell>
                <TableCell>{translate('products.variant_status')}</TableCell>
                <TableCell>{translate('products.made_to_order')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drafts.map((row) => {
                const title = buildVariantTitle(row, options);
                return (
                  <TableRow key={row.id}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.has(row.id)} onChange={() => toggleRow(row.id)} />
                    </TableCell>
                    <TableCell>{title}</TableCell>
                    <TableCell>
                      <CostField
                        size="small"
                        showLabel={false}
                        showHelper={false}
                        value={row.cost ?? 0}
                        onChange={(value) => updateDraft(row.id, { cost: value })}
                        sx={{ width: COMPACT_COST_FIELD_WIDTH }}
                      />
                    </TableCell>
                    <TableCell>
                      <CurrencyTextField
                        size="small"
                        value={row.price ?? 0}
                        onChange={(value) => updateDraft(row.id, { price: value })}
                        sx={{ width: 128 }}
                      />
                    </TableCell>
                    <TableCell>
                      {row.is_made_to_order ? (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      ) : (
                        <TextField
                          type="number"
                          size="small"
                          value={row.stock}
                          onChange={(e) => updateDraft(row.id, { stock: parseFloat(e.target.value) || 0 })}
                          inputProps={{ min: 0, step: 1 }}
                          sx={{ width: 80 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={row.manufacturing_days || ''}
                        placeholder="—"
                        onChange={(e) =>
                          updateDraft(row.id, {
                            manufacturing_days:
                              e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0,
                          })
                        }
                        inputProps={{ min: 0 }}
                        sx={{ width: 72 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={row.status}
                        onChange={(e) => updateDraft(row.id, { status: e.target.value })}
                        sx={{ minWidth: 96 }}
                      >
                        <MenuItem value="ACTIVE">{translate('products.variant_status_active')}</MenuItem>
                        <MenuItem value="INACTIVE">{translate('products.variant_status_inactive')}</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={row.is_made_to_order}
                            onChange={(e) =>
                              updateDraft(row.id, {
                                is_made_to_order: e.target.checked,
                                stock: e.target.checked ? 0 : row.stock,
                              })
                            }
                          />
                        }
                        label=""
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {translate('common.cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || changedRows.length === 0}>
          {saving ? translate('common.saving') : translate('products.bulk_save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
